import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAppEvent } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { Icon } from '../../../concepts/resources/Icon';
import { Fill } from '../../../ui/Fill';
import { AppEvents } from '../../AppEvents';
import { useResolve } from '../../Container';
import { Colors } from './../../../ui';

const Empty = styled(Fill)`
    display: flex;
    align-items: center;
    justify-content: center;
    background: #F6FAFD;
    color: ${Colors.textLight};
`;

const PrivateFrame = styled(Fill)`
    display: flex;
    align-items: center;
    justify-content: center;
    background: #F6FAFD;
    color: ${Colors.textLight};
`;

const Frame = styled.div`
    position: absolute;
    inset: 0;
    background-size: cover;
`;

export const BoardPreviewPrivate = () =>
    <PrivateFrame>
        <Icon size="2" src="deqo://icons/hero/lock-closed" />
    </PrivateFrame>
    ;

export type BoardIdWithOptionalThumb = { id?: string, thumbUrl?: string };
export type BoardThumbProps = { info: BoardIdWithOptionalThumb };

export const BoardThumb = React.memo<BoardThumbProps>(({ info }) => {

    const api = useResolve(ApiClient);
    const events = useResolve(AppEvents);
    const timeout = useRef<any>();

    const [url, setUrl] = useState(info.thumbUrl);
    const [legacy, setLegacy] = useState(false);

    const updateUrl = async () => {
        const resp = await api.exec(v2.boards.thumb(info.id));
        setUrl(resp.url);
        setLegacy(!!resp.legacy);
    };

    useEffect(() => {
        if (!url) updateUrl();
        return () => clearTimeout(timeout.current);
    }, []);

    useEffect(() => events.on('board:thumb', ({ boardId }) => {
        if (boardId === info.id) {
            updateUrl();
        }
    }), []);

    // So Chrome (on Win11 at least) appears to stretch images at different quality using img vs bg-image.  // Use img unless we can't!
    return !!url ? (legacy ? <Frame style={{ backgroundImage: `url(${url})` }} /> : <img src={url} />) : <Empty />;
});

export const useBoardThumb = ({ id, thumbUrl }: BoardIdWithOptionalThumb = {}) => {

    const api = useResolve(ApiClient);
    const [url, setUrl] = useState(thumbUrl);

    const fetchUrl = useCallback(async () => {
        if (!id) return;
        const resp = await api.exec(v2.boards.thumb(id));
        setUrl(resp.url);
    }, [id]);

    useEffect(() => {
        if (!url) fetchUrl();
    }, [id]);

    useAppEvent('board:thumb', useCallback(({ boardId }) => {
        if (boardId === id) {
            fetchUrl();
        }
    }, [id]));

    return url;
};
